// FILE: src/pages/Mobile/MobileAttendancePage.js
// Monthly attendance matrix for Android APK.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, IconButton, Stack,
  CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const getLocalDate = () => {
  const now = new Date();
  return new Date(now - now.getTimezoneOffset() * 60000).toISOString().split('T')[0];
};

const MobileAttendancePage = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cycleOffset, setCycleOffset] = useState(0);

  const todayStr = getLocalDate();

  const { dateRange, cycleLabel, presentCount, absentCount } = useMemo(() => {
    const today = new Date();
    let baseMonth = today.getMonth();
    let baseYear  = today.getFullYear();
    if (today.getDate() < 21) baseMonth -= 1;
    baseMonth += cycleOffset;
    if (baseMonth < 0)  { baseMonth += 12; baseYear -= 1; }
    if (baseMonth > 11) { baseMonth -= 12; baseYear += 1; }
    const start = new Date(Date.UTC(baseYear, baseMonth, 21));
    const end   = new Date(Date.UTC(baseYear, baseMonth + 1, 20));
    const totalDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const dates = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }
    const label = `${start.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} – ${end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;

    // Count present/absent for summary
    let p = 0, a = 0;
    // calculated after history loads
    return { dateRange: dates, cycleLabel: label, presentCount: p, absentCount: a };
  }, [cycleOffset]);

  const fetchHistory = useCallback(async () => {
    if (!user?.employeeId) return;
    setLoading(true);
    try {
      const res = await api.get(`/attendance/employee/${user.employeeId}`);
      setHistory(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user?.employeeId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const getStatus = (date) => {
    const rec = history.find(r => r.date === date);
    if (rec?.displayStatus) {
      switch (rec.displayStatus.toUpperCase()) {
        case 'P':   return { label: 'P', color: '#16a34a', bg: '#f0fdf4' };
        case 'A':   return { label: 'A', color: '#dc2626', bg: '#fef2f2' };
        case 'W': case 'W/O': return { label: 'W', color: '#ca8a04', bg: '#fefce8' };
        case 'H':   return { label: 'H', color: '#4f46e5', bg: '#ede9fe' };
        case 'L':   return { label: 'L', color: '#db2777', bg: '#fdf2f8' };
        case 'HD':  return { label: 'HD', color: '#0284c7', bg: '#eff6ff' };
        default:    break;
      }
    }
    const d = new Date(date + 'T00:00:00');
    const today = new Date(todayStr + 'T00:00:00');
    if (d.getDay() === 0) return { label: 'W', color: '#ca8a04', bg: '#fefce8' };
    if (d > today)        return { label: '·', color: '#cbd5e1', bg: '#f8fafc' };
    if (d < today)        return { label: 'A', color: '#dc2626', bg: '#fef2f2' };
    return { label: '–', color: '#94a3b8', bg: '#f8fafc' };
  };

  // Summary counts
  const summary = useMemo(() => {
    const today = new Date(todayStr + 'T00:00:00');
    let p = 0, a = 0, w = 0, l = 0;
    dateRange.forEach(date => {
      const d = new Date(date + 'T00:00:00');
      if (d > today) return;
      const s = getStatus(date);
      if (s.label === 'P')  p++;
      else if (s.label === 'A') a++;
      else if (s.label === 'W') w++;
      else if (s.label === 'L') l++;
    });
    return { p, a, w, l };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, history, todayStr]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>

      <Typography fontSize={22} fontWeight={900} mb={0.5}>My Attendance</Typography>

      {/* Cycle navigation */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <IconButton size="small" onClick={() => setCycleOffset(cycleOffset - 1)}>
          <ArrowBackIos fontSize="small" />
        </IconButton>
        <Typography fontWeight={700} fontSize={13} textAlign="center">{cycleLabel}</Typography>
        <IconButton size="small" disabled={cycleOffset >= 0} onClick={() => setCycleOffset(cycleOffset + 1)}>
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Stack>

      {/* Summary chips */}
      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={1}>
        <Chip label={`✅ Present: ${summary.p}`} size="small" sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 700, fontSize: 12 }} />
        <Chip label={`❌ Absent: ${summary.a}`}  size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: 12 }} />
        <Chip label={`🟡 Week Off: ${summary.w}`} size="small" sx={{ bgcolor: '#fefce8', color: '#ca8a04', fontWeight: 700, fontSize: 12 }} />
        {summary.l > 0 && <Chip label={`🏖 Leave: ${summary.l}`} size="small" sx={{ bgcolor: '#fdf2f8', color: '#db2777', fontWeight: 700, fontSize: 12 }} />}
      </Stack>

      {/* Calendar grid — 7 days per row */}
      <Paper sx={{ p: 1.5, borderRadius: 3 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5
        }}>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <Box key={i} sx={{ textAlign: 'center', pb: 0.5 }}>
              <Typography fontSize={10} fontWeight={700} color="text.secondary">{d}</Typography>
            </Box>
          ))}

          {/* Empty cells before first date */}
          {(() => {
            const firstDay = new Date(dateRange[0] + 'T00:00:00').getDay();
            return Array.from({ length: firstDay }).map((_, i) => <Box key={`empty-${i}`} />);
          })()}

          {dateRange.map(date => {
            const s = getStatus(date);
            const day = parseInt(date.slice(8, 10));
            const isToday = date === todayStr;
            return (
              <Box key={date} sx={{
                textAlign: 'center', p: 0.3,
                borderRadius: 1.5,
                bgcolor: s.bg,
                border: isToday ? '2px solid #0284c7' : '1px solid transparent',
              }}>
                <Typography fontSize={9} color="text.secondary" lineHeight={1.2}>{day}</Typography>
                <Typography fontSize={11} fontWeight={800} color={s.color} lineHeight={1.4}>{s.label}</Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Legend */}
      <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" gap={0.5} justifyContent="center">
        {[
          { label: 'P = Present',  color: '#16a34a' },
          { label: 'A = Absent',   color: '#dc2626' },
          { label: 'W = Week Off', color: '#ca8a04' },
          { label: 'H = Holiday',  color: '#4f46e5' },
          { label: 'L = Leave',    color: '#db2777' },
        ].map(l => (
          <Typography key={l.label} fontSize={10} color={l.color} fontWeight={600}>{l.label}</Typography>
        ))}
      </Stack>
    </Box>
  );
};

export default MobileAttendancePage;